// The non-null assertions are acceptable because there definitely will always
// be data in the successful responses where the non-null assertions are made.
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/dist/query';
import { BaseQueryFn } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import {
  BigCommerceEndpointTagTypes,
  BulkPricingRule,
  FailedProductResponse,
  JimboResponse,
  Product,
  ProductCategory,
  ProductImage,
  ProductRankingUpdate,
  UploadProgress,
} from '@models';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const productEndpoints = (
  builder: EndpointBuilder<
    // eslint-disable-next-line @typescript-eslint/ban-types
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta>,
    BigCommerceEndpointTagTypes,
    'bigCommerceApi'
  >,
) => {
  return {
    getAllProducts: builder.query<Product[], void>({
      query: () => 'products',
      providesTags: ['Product'],
    }),

    getAllProductCategories: builder.query<ProductCategory[], void>({
      query: () => 'products/categories',
    }),

    getUploadCreateProgress: builder.query<JimboResponse<UploadProgress>, undefined>({
      query: () => 'products/upload-create-progress',
    }),

    getUploadUpdateProgress: builder.query<JimboResponse<UploadProgress>, undefined>({
      query: () => 'products/upload-update-progress',
    }),

    getBulkPricingRules: builder.query<BulkPricingRule[], number>({
      query: (productId) => `products/${productId}/bulk-pricing-rules`,
      transformResponse: (response: JimboResponse<BulkPricingRule[]>) => response.data!,
    }),

    getSyncProduct: builder.query<string[], void>({
      query: () => `products/sync-product`,
      transformResponse: (response: JimboResponse<string[]>) => response.data!,
    }),

    createBulkPricingRule: builder.mutation<BulkPricingRule, { productId: number; rule: Partial<BulkPricingRule> }>({
      query: ({ productId, rule }) => ({
        url: `products/${productId}/bulk-pricing-rules`,
        method: 'POST',
        body: rule,
      }),
      invalidatesTags: ['Product'],
      transformResponse: (response: JimboResponse<BulkPricingRule>) => response.data!,
    }),

    updateBulkPricingRule: builder.mutation<BulkPricingRule, { productId: number; rule: BulkPricingRule }>({
      query: ({ productId, rule }) => ({
        url: `products/${productId}/bulk-pricing-rules`,
        method: 'PUT',
        body: rule,
      }),
      invalidatesTags: ['Product'],
      transformResponse: (response: JimboResponse<BulkPricingRule>) => response.data!,
    }),

    createProduct: builder.mutation<Product, Partial<Product>>({
      query: (product) => ({
        url: 'products',
        method: 'POST',
        body: product,
      }),
      invalidatesTags: ['Product'],
      transformResponse: (response: JimboResponse<Product>) => response.data!,
    }),

    createBatchOfProducts: builder.mutation<
      JimboResponse<{ createdProducts: Product[]; failedProducts: FailedProductResponse[] }>,
      Partial<Product>[]
    >({
      query: (products) => ({
        url: 'products',
        method: 'POST',
        body: products,
      }),
      invalidatesTags: ['Product'],
    }),

    updateProduct: builder.mutation<Product, Product>({
      query: (product) => ({
        url: 'products',
        method: 'PUT',
        body: product,
      }),
      invalidatesTags: ['Product'],
      transformResponse: (response: JimboResponse<Product>) => response.data!,
    }),

    updateBatchOfProducts: builder.mutation<JimboResponse<Product[]>, Partial<Product>[] | ProductRankingUpdate[]>({
      query: (products) => ({
        url: 'products',
        method: 'PUT',
        body: products,
      }),
      invalidatesTags: ['Product'],
    }),

    deleteProduct: builder.mutation<JimboResponse, number>({
      query: (productId) => ({
        url: `products/${productId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Product'],
    }),

    deleteProductCustomField: builder.mutation<JimboResponse, { productId: number; customFieldId: number }>({
      query: ({ productId, customFieldId }) => ({
        url: `products/${productId}/custom-fields/${customFieldId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Product'],
    }),

    getAllProductImages: builder.query<ProductImage[], number>({
      query: (productId) => ({
        url: `products/${productId}/images`,
        method: 'GET',
      }),
      providesTags: ['ProductImage'],
    }),

    uploadProductImage: builder.mutation<JimboResponse, { productId: number; formData: FormData }>({
      query: ({ productId, formData }) => ({
        url: `products/${productId}/images`,
        method: 'POST',
        body: formData,
        headers: {
          //  set Content-Type to undefined so that your browser or whatever client you're using can set it and add that boundary value in there for you
          'content-type': undefined,
        },
      }),
      invalidatesTags: ['ProductImage'],
    }),

    deleteProductImage: builder.mutation<JimboResponse, { productId: number; imageId: number }>({
      query: ({ productId, imageId }) => ({
        url: `products/${productId}/images/${imageId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ProductImage'],
    }),
  };
};
